import { Logger } from './logger.service';

const SW_URL = '/notification-sw.js';

export type PermissionState = NotificationPermission | 'unsupported';

export interface ShowOptions {
  body?: string;
  tag?: string;
  icon?: string;
  /** Custom payload; `rid`/`url` are read by notification-sw.js on click. */
  data?: Record<string, unknown>;
  /** Close automatically after this many ms. Omit to leave it to the OS. */
  autoCloseMs?: number;
  /** Sound is handled separately by AudioNotificationService, so this defaults to true. */
  silent?: boolean;
}

/**
 * Single owner of browser (OS-level) notifications.
 *
 * Everything goes through ServiceWorkerRegistration.showNotification() rather than
 * `new Notification()`, because the latter throws on Chrome for Android — the reason
 * Android users previously got sound but never a visible notification. The direct
 * constructor is kept only as a fallback for browsers with no service worker support.
 *
 * Permission is never requested here on load. Firefox blocks non-gesture prompts
 * outright, Safari requires a gesture, and Chrome degrades them to easily-missed
 * "quieter" UI — which left a large cohort permanently unsubscribed without ever
 * seeing a prompt. requestPermission() must be called from a real user gesture.
 */
class BrowserNotificationService {
  private registration: ServiceWorkerRegistration | null = null;
  private initPromise: Promise<void> | null = null;

  /** Cap on waiting for worker activation, after which we fall back to the constructor. */
  private static readonly SW_READY_TIMEOUT_MS = 5_000;

  get isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  get permission(): PermissionState {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  get isGranted(): boolean {
    return this.permission === 'granted';
  }

  /** Registers the worker. Safe to call repeatedly; only the first call does work. */
  init(): Promise<void> {
    if (!this.initPromise) this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      Logger.debug('[BrowserNotification] No service worker support — using constructor fallback');
      return;
    }
    try {
      await this.removeStaleOneSignalWorkers();
      await navigator.serviceWorker.register(SW_URL);

      // register() resolves as soon as the script is fetched — the worker may still be
      // installing, and showNotification() on a registration with no *active* worker throws.
      // serviceWorker.ready resolves only once an active registration exists, so awaiting it
      // is what makes the first notification after a page load reliable.
      //
      // It is raced against a timeout because `ready` never rejects: if the worker fails to
      // activate (a throwing install handler, for instance) it simply never settles, which
      // would leave this promise — and therefore every show() awaiting init() — pending
      // forever. Silently losing all browser notifications is precisely the failure class
      // NGE-988 set out to remove, so time out and fall back to the constructor instead.
      this.registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), BrowserNotificationService.SW_READY_TIMEOUT_MS)
        ),
      ]);

      if (this.registration) {
        Logger.debug('[BrowserNotification] Worker registered and active');
      } else {
        Logger.debug(
          '[BrowserNotification] Worker did not activate in time — using constructor fallback'
        );
      }
    } catch (err) {
      // Non-fatal: show() falls back to the constructor, which works everywhere but Android.
      Logger.debug('[BrowserNotification] Worker registration failed:', err);
    }
  }

  /**
   * Unregister any leftover OneSignal worker. Without this, a browser that installed it
   * before NGE-988 keeps it alive indefinitely, and it would go on displaying pushes from
   * whatever subscriptions OneSignal still holds — notifications this app can no longer
   * see, gate, or deduplicate.
   */
  private async removeStaleOneSignalWorkers(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations
          .filter((r) => {
            const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL;
            return !!url && url.toLowerCase().includes('onesignal');
          })
          .map(async (r) => {
            Logger.debug('[BrowserNotification] Unregistering stale OneSignal worker');
            await r.unregister();
          })
      );
    } catch (err) {
      Logger.debug('[BrowserNotification] Stale worker cleanup failed:', err);
    }
  }

  /** Must be invoked from a user gesture (click/keypress) to be reliable. */
  async requestPermission(): Promise<PermissionState> {
    if (!this.isSupported) return 'unsupported';
    if (Notification.permission !== 'default') return Notification.permission;
    try {
      const result = await Notification.requestPermission();
      if (result === 'granted') await this.init();
      return result;
    } catch (err) {
      Logger.debug('[BrowserNotification] requestPermission failed:', err);
      return Notification.permission;
    }
  }

  /**
   * Show a browser notification. Callers are responsible for having already checked the
   * user's `browser` channel preference — this method only enforces platform capability.
   */
  async show(title: string, options: ShowOptions = {}): Promise<void> {
    if (!this.isGranted) {
      Logger.debug(`[BrowserNotification] Suppressed "${title}" — permission=${this.permission}`);
      return;
    }

    const { autoCloseMs, ...rest } = options;
    const init: NotificationOptions = {
      body: rest.body,
      tag: rest.tag,
      // The previous code pointed at /favicon.ico, which does not exist in public/ —
      // every notification rendered with a missing icon.
      icon: rest.icon ?? '/favicons/android-icon-192x192.png',
      data: rest.data,
      // Sound is owned by AudioNotificationService so the mute switch and volume
      // slider apply uniformly; letting the OS chime too would double up.
      silent: rest.silent ?? true,
    };

    await this.init();

    if (this.registration) {
      await this.registration.showNotification(title, init);
      if (autoCloseMs && rest.tag) this.scheduleClose(rest.tag, autoCloseMs);
      return;
    }

    // No worker (or registration failed): direct constructor. Throws on Chrome Android,
    // hence the try/catch — a failure here must not break the caller's flow.
    try {
      const notification = new Notification(title, init);
      if (autoCloseMs) setTimeout(() => notification.close(), autoCloseMs);
    } catch (err) {
      Logger.debug(`[BrowserNotification] Constructor fallback failed for "${title}":`, err);
    }
  }

  private scheduleClose(tag: string, delayMs: number): void {
    setTimeout(() => {
      void this.registration
        ?.getNotifications({ tag })
        .then((list) => list.forEach((n) => n.close()))
        .catch(() => {});
    }, delayMs);
  }
}

export const BrowserNotificationServiceInstance = new BrowserNotificationService();
